import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Activity, 
  CheckCircle2, 
  RefreshCw, 
  AlertCircle
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

// The exact 15-stage SOC Use Case workflow sequence
const WORKFLOW_STAGES = [
  { step: 1, key: 'CREATED', label: '1. Created', persona: 'Author / Aravind' },
  { step: 2, key: 'VALIDATED', label: '2. Validated', persona: 'Logesh & Heman' },
  { step: 3, key: 'COORDINATION_PENDING', label: '3. Coordination', persona: 'Praveen & Seshadri' },
  { step: 4, key: 'SHIFT_LEAD_ASSIGNMENT', label: '4. Shift Lead Assign', persona: 'Shift Leads' },
  { step: 5, key: 'IN_PROGRESS', label: '5. In Progress Dev', persona: 'Assigned Engineer' },
  { step: 6, key: 'SHIFT_LEAD_VERIFICATION', label: '6. Verification (2 Tests)', persona: 'Shift Leads' },
  { step: 7, key: 'SHIFT_LEAD_REVIEW', label: '7. Shift Lead Review', persona: 'Shift Leads' },
  { step: 8, key: 'TECHNICAL_REVIEW', label: '8. Technical Review', persona: 'Senior Reviewer' },
  { step: 9, key: 'UAT_APPROVED', label: '9. UAT Approved', persona: 'Shift Lead / Reviewer' },
  { step: 10, key: 'UAT_DEPLOYED', label: '10. UAT Deployed (5 Artifacts)', persona: 'Deployer' },
  { step: 11, key: 'WEEKLY_REVIEW_COMPLETED', label: '11. Weekly Review (Day 7)', persona: 'PMR Consolidators' },
  { step: 12, key: 'POST_MEETING_REPORT', label: '12. Post Meeting Report', persona: 'Praveen & Seshadri' },
  { step: 13, key: 'FINAL_APPROVED', label: '13. Final Approval', persona: 'Aravind (Exec Sign-Off)' },
  { step: 14, key: 'PRODUCTION_APPROVED', label: '14. Production Approved', persona: 'Logesh & Heman' },
  { step: 15, key: 'PRODUCTION_DEPLOYED', label: '15. Production Deployed', persona: 'Live Release' }
];

export function UserInProgressView() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  const [inProgressList, setInProgressList] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInProgress = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = session?.access_token || localStorage.getItem('compass_token');
      const res = await fetch('/api/user/in-progress', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to load in-progress cases');
      const data = await res.json();
      if (data.success) {
        const list = data.data || [];
        setInProgressList(list);

        if (focusId) {
          const matched = list.find((c: any) => c.id === focusId);
          if (matched) setSelectedCase(matched);
        } else if (list.length > 0 && !selectedCase) {
          setSelectedCase(list[0]);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInProgress();
  }, [session, focusId]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-500" />
            <span>Workflow In Progress & Stage Stepper</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Real-time visual tracker across the strict 15-stage SOC Use Case engineering lifecycle.
          </p>
        </div>

        <button
          onClick={fetchInProgress}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 text-xs flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="p-16 flex flex-col items-center justify-center text-muted-foreground gap-3 rounded-xl border border-border bg-card">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
          <span className="text-xs">Loading active workflow states...</span>
        </div>
      ) : inProgressList.length === 0 ? (
        <div className="p-16 text-center text-muted-foreground rounded-xl border border-border bg-card">
          <CheckCircle2 className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm font-medium text-foreground">No active rules currently in progress</p>
          <p className="text-xs mt-1">All your detection use cases are either completed or saved as drafts.</p>
          <button
            onClick={() => navigate('/user/use-cases/create')}
            className="mt-4 px-3.5 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Create Use Case
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Rules List (Left column) */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-xs font-bold uppercase tracking-wide text-muted-foreground px-1">
              Active Use Cases ({inProgressList.length})
            </h2>

            <div className="space-y-2 max-h-[70vh] overflow-y-auto custom-scrollbar pr-1">
              {inProgressList.map((uc) => {
                const isSelected = selectedCase?.id === uc.id;
                return (
                  <div
                    key={uc.id}
                    onClick={() => setSelectedCase(uc)}
                    className={cn(
                      "p-3.5 rounded-xl border cursor-pointer transition-all shadow-xs",
                      isSelected 
                        ? "border-blue-500 bg-blue-500/10 shadow-sm shadow-blue-500/10" 
                        : "border-border bg-card hover:bg-muted/40"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-xs text-blue-500">{uc.number}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted text-foreground border border-border">
                        Step {uc.current_step}/15
                      </span>
                    </div>

                    <h4 className="text-xs font-bold text-foreground mt-1.5 line-clamp-1">
                      {uc.title}
                    </h4>

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50 text-[10px] text-muted-foreground">
                      <span>Status: <strong className="text-foreground">{uc.status}</strong></span>
                      <span className="text-emerald-500 font-semibold">{uc.sla_status || 'ON_TRACK'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed 15-Stage Workflow Stepper (Right column) */}
          <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 shadow-xs space-y-6">
            {selectedCase ? (
              <>
                {/* Header for Selected Case */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border pb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm text-blue-500">{selectedCase.number}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 font-semibold">
                        Current: Step {selectedCase.current_step} - {selectedCase.status}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-foreground mt-1">{selectedCase.title}</h3>
                  </div>

                  <div className="text-right">
                    <span className="text-[10px] text-muted-foreground uppercase font-semibold block">SLA Performance</span>
                    <span className="text-xs font-bold text-emerald-500">{selectedCase.sla_status || 'ON_TRACK'}</span>
                  </div>
                </div>

                {/* VISUAL 15-STAGE STEPPER */}
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
                    15-Stage Governance Pipeline
                  </h4>

                  <div className="relative pl-6 space-y-3 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
                    {WORKFLOW_STAGES.map((st) => {
                      const isCompleted = selectedCase.current_step > st.step;
                      const isCurrent = selectedCase.current_step === st.step;
                      const isPending = selectedCase.current_step < st.step;

                      return (
                        <div key={st.step} className="relative flex items-start gap-3 group">
                          {/* Dot indicator */}
                          <div className={cn(
                            "absolute -left-6 mt-0.5 flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold transition-all",
                            isCompleted && "bg-emerald-500 border-emerald-500 text-white shadow-xs",
                            isCurrent && "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-500/40 ring-4 ring-blue-500/20 animate-pulse",
                            isPending && "bg-muted border-border text-muted-foreground"
                          )}>
                            {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : st.step}
                          </div>

                          {/* Stage details card */}
                          <div className={cn(
                            "flex-1 p-3 rounded-xl border text-xs transition-all",
                            isCompleted && "bg-emerald-500/5 border-emerald-500/20 text-foreground",
                            isCurrent && "bg-blue-500/10 border-blue-500/40 text-foreground shadow-xs",
                            isPending && "bg-muted/20 border-border/60 text-muted-foreground opacity-60"
                          )}>
                            <div className="flex items-center justify-between">
                              <span className={cn(
                                "font-bold",
                                isCurrent ? "text-blue-500" : isCompleted ? "text-emerald-500" : "text-muted-foreground"
                              )}>
                                {st.label}
                              </span>
                              <span className={cn(
                                "text-[10px] font-semibold px-2 py-0.5 rounded-full border",
                                isCompleted && "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
                                isCurrent && "bg-blue-600 text-white border-blue-600",
                                isPending && "bg-muted text-muted-foreground border-border"
                              )}>
                                {isCompleted ? 'Completed' : isCurrent ? 'Active Stage' : 'Pending'}
                              </span>
                            </div>

                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              Responsible Persona: <strong className="text-foreground">{st.persona}</strong>
                            </p>

                            {isCurrent && (
                              <div className="mt-2 pt-2 border-t border-blue-500/20 flex items-center justify-between text-[11px] text-blue-500 font-medium">
                                <span>Currently undergoing verification at this gate</span>
                                {selectedCase.active_engineer_name && (
                                  <span>Engineer: {selectedCase.active_engineer_name}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="p-12 text-center text-muted-foreground">
                <p>Select a use case on the left to inspect its 15-stage workflow stepper.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
